"use client";

import { useSession } from "next-auth/react";
import { api } from "~/trpc/react";

/**
 * Hook to get current user's organisation info and booking eligibility.
 */
export function useOrganisation() {
  const { data: session } = useSession();

  const { data: orgInfo, isLoading: isLoadingOrg } =
    api.organisation.getMyOrganisation.useQuery(undefined, {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      enabled: !!session?.user,
    });

  const { data: canBook, isLoading: isLoadingCanBook } =
    api.organisation.canBookFromPool.useQuery(undefined, {
      staleTime: 1 * 60 * 1000, // Cache for 1 minute (balance can change)
      enabled: !!orgInfo, // Only check if user has org
    });

  const isOrgMember = !!orgInfo;
  const isOrgSponsored = canBook?.canBook ?? false;
  const organisationName = orgInfo?.organisationName ?? null;
  const sessionBalance = orgInfo?.sessionBalance ?? 0;

  return {
    // State
    isLoading: isLoadingOrg || (isOrgMember && isLoadingCanBook),
    isOrgMember,
    isOrgSponsored,
    organisationName,
    sessionBalance,
    canBookReason: canBook?.reason,

    // Computed helpers
    /**
     * Returns true if user should see prices hidden and "Covered by [OrgName]" badge
     */
    shouldHidePrice: isOrgSponsored,
  };
}

/**
 * Hook to get organisation stats (for admins).
 */
export function useOrganisationStats(organisationId: string | null) {
  return api.organisation.getStats.useQuery(
    { organisationId: organisationId! },
    {
      enabled: !!organisationId,
      staleTime: 30 * 1000, // Cache for 30 seconds
    }
  );
}
